import type {
  ClientContactView,
  RepresentativeInvitationConfig,
  RepresentativeInvitationSummary,
} from "@closer/shared";
import {
  createRepresentativeInvitationSchema,
  REPRESENTATIVE_INVITATION_STATUS_NAMES,
} from "@closer/shared";
import {
  Badge,
  Button,
  Card,
  FormField,
  Input,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { applyServerErrors, useZodForm } from "../../lib/useZodForm";

/**
 * MÜVEKKİL TEMSİLCİSİ DAVETİ (CLOS-177 `[124]`).
 *
 * ### ⚠ Kabul YETKİ DEĞİLDİR
 *
 * Ekran bunu açıkça yazıyor. Davet yalnız "bu e-posta bu müvekkil için çağrıldı"
 * bilgisidir; gerçek temsil ilişkisi CLOS-178'in ayrı komutuyla kurulur. Kabul edilmiş bir
 * daveti "temsilci" diye göstermek, henüz verilmemiş bir yetkiyi verilmiş sanmaktır.
 *
 * ### ⚠ Kapsam ÖNERİDİR
 *
 * Alanın adı "önerilen temsil kapsamı": davetin metni bağlayıcı değil, temsil kurulurken
 * seçilen kapsam anahtarı bağlayıcı. İkisi tek alan olsaydı davetteki serbest cümle yetki
 * sınırıymış gibi okunurdu.
 *
 * ### ⚠ Kanıt türü sözlüğü KONFİGDEN
 *
 * Belge türleri `/api/clients/representative-policy` üzerinden geliyor; ekran kendi
 * listesini tutsaydı konfige eklenen bir tür arayüzde görünmezdi.
 */
export function ClientRepresentativeInvitations({
  clientId,
  arsivde,
  alanKurulu,
}: {
  clientId: string;
  /** ⚠ Sunucu arşivli müvekkile davet göndermeyi reddediyor; ekran teklif de etmiyor. */
  arsivde: boolean;
  /**
   * Sponsorlu Müvekkil Alanı kurulmuş mu (CLOS-865).
   *
   * ⚠ ÇIKMAZ SOKAĞI KAPATIYOR. Sunucu alan yokken daveti doğru şekilde 409 ile reddediyor
   * ve *"önce Sponsorlu Müvekkil Alanı kurulmalı"* diyor — ama o cümlenin işaret ettiği
   * eylemin arayüzde hiçbir karşılığı yoktu. Kullanıcı formu dolduruyor, gönderiyor,
   * hatayı okuyor ve yapacak bir şey bulamıyordu.
   *
   * `undefined` = HENÜZ BİLİNMİYOR (sorgu sürüyor). `false` ile karıştırmıyoruz: bilgi
   * gelmeden "alan yok" demek, alanı olan bir müvekkilde daveti gereksiz yere kilitlerdi.
   */
  alanKurulu: boolean | undefined;
}) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();
  const queryClient = useQueryClient();
  const [formAcik, setFormAcik] = useState(false);

  const yetkili = can("client.representative");
  const form = useZodForm(createRepresentativeInvitationSchema);

  const anahtar = tenantKey(tenantId, "client-rep-invitations", clientId);
  const davetler = useQuery({
    enabled: yetkili,
    queryKey: anahtar,
    queryFn: async () =>
      (
        await api<RepresentativeInvitationSummary[]>(
          `/api/clients/${clientId}/representative-invitations`,
          { tenantId },
        )
      ).data,
  });

  const politika = useQuery({
    enabled: yetkili,
    queryKey: tenantKey(tenantId, "representative-policy"),
    queryFn: async () =>
      (
        await api<RepresentativeInvitationConfig>("/api/clients/representative-policy", {
          tenantId,
        })
      ).data,
  });

  /** ⚠ CLOS-174 işareti burada işe yarıyor: aday kişiler seçim kolaylığı, yetki değil. */
  const adaylar = useQuery({
    enabled: yetkili,
    queryKey: tenantKey(tenantId, "client-contacts", clientId),
    queryFn: async () =>
      (await api<ClientContactView[]>(`/api/clients/${clientId}/contacts`, { tenantId })).data,
  });

  const yenile = () => queryClient.invalidateQueries({ queryKey: anahtar });

  const davetEt = useMutation({
    mutationFn: (girdi: Record<string, unknown>) =>
      api<RepresentativeInvitationSummary>(`/api/clients/${clientId}/representative-invitations`, {
        method: "POST",
        tenantId,
        body: JSON.stringify(girdi),
      }),
    onSuccess: async () => {
      setFormAcik(false);
      form.reset();
      await yenile();
      toast.success("Temsilci daveti gönderildi");
    },
    onError: (hata: unknown) => {
      if (hata instanceof ApiRequestError && hata.fields) {
        applyServerErrors(form, hata);
        return;
      }
      toast.error(hata instanceof ApiRequestError ? hata.message : "Davet gönderilemedi");
    },
  });

  const iptalEt = useMutation({
    mutationFn: (inviteId: string) =>
      api(`/api/clients/${clientId}/representative-invitations/${inviteId}/cancel`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({}),
      }),
    onSuccess: async () => {
      await yenile();
      toast.success("Davet iptal edildi");
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Davet iptal edilemedi"),
  });

  if (!yetkili) return null;

  const gonder = form.handleSubmit((girdi) => davetEt.mutate(girdi as Record<string, unknown>));

  /**
   * ⚠ Boş seçim `undefined`'a çevriliyor, boş dizeye değil: şema bu alanları isteğe bağlı
   * ama BOŞ OLMAYAN metin olarak tanımlıyor. Ham `""` gönderilseydi form kullanıcının
   * doldurmadığı bir alan yüzünden doğrulamaya takılır, hata da görünmez bir alanda çıkardı.
   */
  const bosSecim = { setValueAs: (deger: string) => (deger === "" ? undefined : deger) };

  const kanitTurleri = politika.data?.evidenceDocumentTypes ?? [];
  const adayKisiler = (adaylar.data ?? []).filter((k) => k.isRepresentativeCandidate);

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <h2 className="mr-auto text-base font-semibold text-fg-heading">Temsilci davetleri</h2>
          {!arsivde && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              /**
               * ⚠ Alan yokken KAPALI, gizli değil (CLOS-865). Gizleseydik kullanıcı
               * özelliğin var olduğunu hiç öğrenemez ve neden yapamadığını da bilemezdi;
               * kapalı düğme + açıklama, sunucunun 409 mesajını eyleme çevirilebilir
               * hâle getiriyor.
               */
              disabled={alanKurulu === false}
              onClick={() => setFormAcik((a) => !a)}
            >
              {formAcik ? "Vazgeç" : "Temsilci davet et"}
            </Button>
          )}
        </div>

        {/*
          ⚠ Alan yoksa SEBEP ve ÇÖZÜM aynı cümlede (CLOS-865). Sunucunun 409'u doğruydu ama
          arayüzde karşılığı yoktu; kullanıcı ne yapacağını buradan öğreniyor.
        */}
        {!arsivde && alanKurulu === false ? (
          <p data-testid="alan-gerekli-uyarisi" className="text-sm text-fg-muted">
            Temsilci davet edebilmek için önce yukarıdaki <strong>Müvekkil Alanı</strong> bölümünden
            alanı kurun.
          </p>
        ) : null}

        {/* ⚠ Kabul YETKİ DEĞİL: ekran bunu cümleyle söylüyor, kullanıcı çıkarım yapmıyor. */}
        <p className="text-xs text-fg-soft">
          Davetin kabul edilmesi temsil yetkisi vermez; yetki, kabul edilmiş davetten temsil
          ilişkisi kurulduğunda başlar.
        </p>

        {arsivde && (
          <p className="text-xs text-fg-soft">
            Müvekkil arşivde: yeni temsilci daveti gönderilemez. Gönderilmiş davetler ve kurulmuş
            temsil ilişkileri geçerliliğini koruyor.
          </p>
        )}

        {formAcik && !arsivde && (
          <form noValidate className="flex flex-col gap-2" onSubmit={gonder}>
            <FormField label="E-posta" error={form.formState.errors.email?.message} required>
              <Input type="email" {...form.register("email")} />
            </FormField>

            {adayKisiler.length > 0 && (
              <FormField
                label="İletişim kişisi"
                hint="CLOS-174'te temsilci adayı işaretlenmiş kişiler; seçim yetki vermez."
                error={form.formState.errors.contactId?.message}
              >
                <Select {...form.register("contactId", bosSecim)}>
                  <option value="">Seçiniz</option>
                  {adayKisiler.map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.fullName}
                    </option>
                  ))}
                </Select>
              </FormField>
            )}

            <FormField
              label="Önerilen temsil kapsamı"
              error={form.formState.errors.proposedScope?.message}
              required
            >
              <Textarea rows={3} {...form.register("proposedScope")} />
            </FormField>

            {kanitTurleri.length > 0 && (
              <FormField
                label="Temsil kanıtı"
                error={form.formState.errors.evidenceType?.message}
                hint="Belge türleri konfigden gelir."
              >
                <Select {...form.register("evidenceType", bosSecim)}>
                  <option value="">Seçiniz</option>
                  {kanitTurleri.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}
                    </option>
                  ))}
                </Select>
              </FormField>
            )}

            <div className="flex justify-end">
              <Button type="submit" size="sm" loading={davetEt.isPending}>
                Daveti gönder
              </Button>
            </div>
          </form>
        )}

        {davetler.isPending ? (
          <div role="status" aria-label="Temsilci davetleri yükleniyor">
            <Skeleton lines={2} />
          </div>
        ) : davetler.error ? (
          <StateView
            state={resolveErrorState(davetler.error, { idempotent: true })}
            onRetry={() => void davetler.refetch()}
          />
        ) : (davetler.data ?? []).length === 0 ? (
          <p className="text-sm text-fg-soft">Bu müvekkil için temsilci daveti gönderilmemiş.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {(davetler.data ?? []).map((davet) => (
              <li key={davet.id} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium text-fg-strong">{davet.email}</span>
                <Badge tone={davet.status === "accepted" ? "success" : "neutral"}>
                  {REPRESENTATIVE_INVITATION_STATUS_NAMES[davet.status]}
                </Badge>
                <span className="text-xs text-fg-soft">
                  {new Date(davet.expiresAt).toLocaleDateString("tr-TR")} tarihine kadar geçerli
                </span>
                {/* ⚠ İptal yalnız YANIT BEKLEYEN davette: karar verilmiş davet geri alınmaz. */}
                {davet.status === "pending" && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="ml-auto"
                    onClick={() => iptalEt.mutate(davet.id)}
                  >
                    Daveti iptal et
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
